import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React, {useCallback} from 'react';

import FileStates from 'FileStates';
import {ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {areReviewLifecyclesEnabledForUser} from 'notebook/reviews/review-lifecycles/ReviewLifecyclesUtil';
import ReviewBadge from 'notebook/reviews/ReviewBadge';
import ReviewHistoryTabState from 'notebook/reviews/ReviewHistoryTabState';
import notifications from 'notifications';
import {withLoadingScreen} from 'util/Promises';

import {
  StudyDesignReviewStatusQuery,
  StudyDesignReviewStatusQueryVariables,
} from './__generated/StudyDesignReviewStatusQuery';

const STUDY_DESIGN_REVIEW_STATUS_POLL_INTERVAL_MS = 2000;

export const STUDY_DESIGN_REVIEW_STATUS_QUERY: DocumentNode = gql`
  query StudyDesignReviewStatusQuery($id: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    study(id: $id) {
      ... on StudyNode {
        id
        studyDesign {
          ... on ReviewableInterface {
            id
            review {
              id
              status
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface StudyDesignReviewBadgeProps {
  studyDesign: {id: string};
  studyId: string;
  sendForReviewValidationError: string | null;
  refetch?: () => Promise<void>;
}

export function useStudyDesignReviewStatusQuery(studyId: string): ReviewStatus | null {
  const result = useBenchlingQuery<StudyDesignReviewStatusQuery, StudyDesignReviewStatusQueryVariables>(
    STUDY_DESIGN_REVIEW_STATUS_QUERY,
    {variables: {id: studyId}, fetchPolicy: 'cache-and-network'}
  );
  if (result.type === QueryResultType.PENDING) {
    return null;
  } else if (result.type === QueryResultType.SUCCESS) {
    const {data} = result;
    const study = data.study;
    if (isInaccessible(study) || !study.studyDesign || !study.studyDesign.review) {
      return null;
    }
    return study.studyDesign.review.status;
  }
  return null;
}

export default function StudyDesignReviewBadge({
  studyId,
  studyDesign,
  sendForReviewValidationError,
  refetch,
}: StudyDesignReviewBadgeProps): JSX.Element {
  const getSendForReviewValidationError = useCallback(
    () => sendForReviewValidationError,
    [sendForReviewValidationError]
  );
  const openHistory = useCallback(async () => {
    const studyFileState = await withLoadingScreen(Promise.resolve(FileStates.get().openFolderItem(studyId)));
    if (!studyFileState) {
      notifications.error('Unable to open study design review history');
      return;
    }
    let reviewTab = studyFileState.findTab(ReviewHistoryTabState);
    if (!reviewTab) {
      reviewTab = new ReviewHistoryTabState({
        fileId: studyDesign.id,
        closeable: true,
        title: 'Design Review',
      });
      studyFileState.buffers().at(0).tabs().add(reviewTab);
    }
    reviewTab.set({active: true});
  }, [studyId, studyDesign.id]);

  const status = useStudyDesignReviewStatusQuery(studyId);
  useSkippableBenchlingQuery<StudyDesignReviewStatusQuery, StudyDesignReviewStatusQueryVariables>(
    STUDY_DESIGN_REVIEW_STATUS_QUERY,
    {
      variables: {id: studyId},
      pollInterval: STUDY_DESIGN_REVIEW_STATUS_POLL_INTERVAL_MS,
      skip:
        status !== ReviewStatus.REVIEW_SNAPSHOT_IN_PROGRESS &&
        status !== ReviewStatus.ACCEPTANCE_SNAPSHOT_IN_PROGRESS,
    }
  );
  return (
    <ReviewBadge
      reviewableId={studyDesign.id}
      enableReviewLifecycle={areReviewLifecyclesEnabledForUser()}
      getSendForReviewValidationError={getSendForReviewValidationError}
      openHistory={openHistory}
      onReviewChange={(reviewableId, review, filePermissions, snapshotTask) => refetch?.()}
    />
  );
}
